
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from './store';
import { Sidebar } from './components/Sidebar';
import { Toasts } from './components/ui/Toasts';
import { ErrorBoundary } from './components/ErrorBoundary';
import { TaskStatus } from './types';

// Lazy load page components for code splitting
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Projects = lazy(() => import('./pages/Projects').then(m => ({ default: m.Projects })));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail').then(m => ({ default: m.ProjectDetail })));
const CommonFiles = lazy(() => import('./pages/CommonFiles').then(m => ({ default: m.CommonFiles })));
const Timeline = lazy(() => import('./pages/Timeline').then(m => ({ default: m.Timeline })));
const GateReview = lazy(() => import('./pages/GateReview').then(m => ({ default: m.GateReview })));
const Tickets = lazy(() => import('./pages/Tickets').then(m => ({ default: m.Tickets })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const TaskPage = lazy(() => import('./pages/TaskPage').then(m => ({ default: m.TaskPage })));
const Templates = lazy(() => import('./pages/Templates').then(m => ({ default: m.Templates })));
const TemplateDetail = lazy(() => import('./pages/TemplateDetail').then(m => ({ default: m.TemplateDetail })));
const CalendarPage = lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const MemberOnboarding = lazy(() => import('./pages/MemberOnboarding').then(m => ({ default: m.MemberOnboarding })));
const DatabasePage = lazy(() => import('./pages/DatabasePage').then(m => ({ default: m.DatabasePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 dark:border-slate-100"></div>
  </div>
);

// Placeholder Pages for demo completeness
const Placeholder = ({ title }: { title: string }) => (
  <div className="flex items-center justify-center h-full text-slate-400 font-medium text-lg bg-slate-50 dark:bg-slate-900 dark:text-slate-500">
    {title} Module Coming Soon
  </div>
);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthLoading } = useAppStore();
  const location = useLocation();

  // Show loader while checking authentication
  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <div className="flex h-screen w-screen bg-background transition-colors duration-300">
      <Sidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden relative">
        {children}
        <Toasts />
      </div>
    </div>
  );
};

const AppRoutes = () => {
  const { user } = useAppStore();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
        
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/projects" element={<PrivateRoute><Projects /></PrivateRoute>} />
        <Route path="/projects/:projectId" element={<PrivateRoute><ProjectDetail /></PrivateRoute>} />
        <Route path="/projects/:projectId/common-files" element={<PrivateRoute><CommonFiles /></PrivateRoute>} />
        <Route path="/projects/:projectId/timeline/:parentTaskId?" element={<PrivateRoute><Timeline /></PrivateRoute>} />
        <Route path="/projects/:projectId/gate" element={<PrivateRoute><GateReview /></PrivateRoute>} />
        <Route path="/projects/:projectId/tasks/:taskId" element={<PrivateRoute><TaskPage /></PrivateRoute>} />
        
        <Route path="/onboarding" element={<PrivateRoute><MemberOnboarding /></PrivateRoute>} />
        
        <Route path="/templates" element={<PrivateRoute><Templates /></PrivateRoute>} />
        <Route path="/templates/:templateId" element={<PrivateRoute><TemplateDetail /></PrivateRoute>} />
        <Route path="/templates/:templateId/phases/:phaseId/:parentTaskId?" element={<PrivateRoute><Timeline /></PrivateRoute>} />
        
        <Route path="/tickets" element={<PrivateRoute><Tickets /></PrivateRoute>} />
        <Route path="/calendar" element={<PrivateRoute><CalendarPage /></PrivateRoute>} />
        
        <Route path="/factory" element={<PrivateRoute><Placeholder title="Factory Dashboard" /></PrivateRoute>} />
        <Route path="/database" element={<PrivateRoute><DatabasePage /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
        
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Suspense>
  );
};

export default function App() {
  const { themeMode, tasks, setTasks, initializeFirebase, mode } = useAppStore();

  // Initialize Firebase on mount
  useEffect(() => {
    if (mode === 'firebase') {
      initializeFirebase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]); // Only depend on mode, not initializeFirebase

  // Handle Theme Application
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('dark', 'light');

    if (themeMode === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(themeMode);
    }
  }, [themeMode]);

  // Handle Task Automation (Blocking logic)
  useEffect(() => {
    if (tasks.length === 0) return;

    let changed = false;
    const next = tasks.map(t => {
      if (t.status === 'COMPLETE') return t;
      
      // Find blocking dependencies
      const blockingDeps = (t.dependsOn || []).filter(id => {
        const dep = tasks.find(d => d.id === id);
        return dep && dep.status !== 'COMPLETE';
      });
      
      const shouldBeBlocked = blockingDeps.length > 0;
      
      if (shouldBeBlocked && t.status !== 'BLOCKED') {
        changed = true;
        return { ...t, status: 'BLOCKED' as TaskStatus };
      } else if (!shouldBeBlocked && t.status === 'BLOCKED') {
        changed = true;
        return { ...t, status: 'NOT_STARTED' as TaskStatus };
      }
      return t;
    });

    if (changed) {
        setTasks(next);
    }
  }, [tasks]); 

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
